<template>
	<div
		ref="$root"
		:class="['progress-border', { indeterminate: isIndeterminate }]"
		role="progressbar"
		:style="cssVars"
		:aria-valuenow="isIndeterminate ? undefined : Math.round((progress ?? 0) * 100)"
		aria-valuemin="0"
		aria-valuemax="100"
		:aria-busy="isIndeterminate ? 'true' : undefined">
		<svg
			v-if="dimensions.width > 0 && dimensions.height > 0"
			class="progress-border-svg"
			:viewBox="svgViewBox"
			preserveAspectRatio="none"
			aria-hidden="true">
			<path
				class="track"
				:d="borderPath"
				fill="none"
				:stroke="trackColor"
				:stroke-width="borderWidth"
				pathLength="100" />
			<path
				class="indicator"
				:d="borderPath"
				fill="none"
				:stroke="strokeColor"
				:stroke-width="borderWidth"
				:stroke-dasharray="dashArray"
				:stroke-dashoffset="dashOffset"
				pathLength="100" />
		</svg>
		
		<div class="progress-border-content">
			<slot />
		</div>
	</div>
</template>

<script setup lang="ts">
	import { useColor, useColorProps } from '@Visma-Real-Estate-Solutions/acquisit-ui-vue/helpers'
	import { computed, onBeforeUnmount, onMounted, ref, type PropType } from 'vue'
	
	const props = defineProps({
		...useColorProps(),
		
		progress: {
			type: Number as PropType<number | null>,
			required: false,
			default: null
		},
		
		borderWidth: {
			type: Number,
			required: false,
			default: 2
		}
	})
	
	const colorHelper = useColor(props)
	
	const $root = ref<HTMLElement | null>(null)
	const dimensions = ref({ width: 0, height: 0 })
	
	let resizeObserver: ResizeObserver | null = null
	
	const updateDimensions = () => {
		if (!$root.value) {
			return
		}
		
		const { width, height } = $root.value.getBoundingClientRect()
		
		dimensions.value = {
			width: Math.max(width, 1),
			height: Math.max(height, 1)
		}
	}
	
	onMounted(() => {
		updateDimensions()
		
		if ($root.value) {
			resizeObserver = new ResizeObserver(updateDimensions)
			resizeObserver.observe($root.value)
		}
	})
	
	onBeforeUnmount(() => {
		resizeObserver?.disconnect()
		resizeObserver = null
	})
	
	const strokeOffset = computed(() => props.borderWidth / 2)
	
	const innerWidth = computed(() => Math.max(dimensions.value.width - props.borderWidth, 0))
	const innerHeight = computed(() => Math.max(dimensions.value.height - props.borderWidth, 0))
	
	const borderPath = computed(() => {
		const x = strokeOffset.value
		const y = strokeOffset.value
		const w = innerWidth.value
		const h = innerHeight.value
		const r = h / 2
		const topCenterX = x + w / 2
		
		if (w <= 0 || h <= 0) {
			return ''
		}
		
		// Clockwise from top center around a pill shape
		return [
			`M ${topCenterX} ${y}`,
			`L ${x + w - r} ${y}`,
			`A ${r} ${r} 0 0 1 ${x + w - r} ${y + h}`,
			`L ${x + r} ${y + h}`,
			`A ${r} ${r} 0 0 1 ${x + r} ${y}`,
			`L ${topCenterX} ${y}`
		].join(' ')
	})
	
	const strokeColor = computed(() => {
		if (props.color) {
			return colorHelper.parse(props.color).cssHex
		}
		
		return colorHelper.primary.value.cssHex
	})
	
	const trackColor = computed(() => 'rgba(0, 0, 0, 0)')
	
	const isIndeterminate = computed(() => props.progress === null)
	
	const cssVars = computed(() => ({
		'--progress-border-width': `${props.borderWidth}px`
	}))
	
	const dashArray = computed(() => isIndeterminate.value ? '25 75' : '100')
	
	const dashOffset = computed(() => {
		if (isIndeterminate.value) {
			return undefined
		}
		
		return 100 - ((props.progress ?? 0) * 100)
	})
	
	const svgViewBox = computed(() => `0 0 ${dimensions.value.width} ${dimensions.value.height}`)
</script>

<style lang="scss" scoped>
	.progress-border {
		position: relative;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		box-sizing: border-box;
	}
	
	.progress-border-svg {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		pointer-events: none;
		overflow: visible;
	}
	
	.indicator {
		transition: stroke-dashoffset 0.15s ease-out;
	}
	
	.indeterminate .indicator {
		animation: progress-border-sweep 1.1s linear infinite;
		transition: none;
	}
	
	.progress-border-content {
		position: relative;
		z-index: 1;
		display: inherit;
		align-items: inherit;
		justify-content: inherit;
		gap: inherit;
		width: 100%;
	}
	
	@keyframes progress-border-sweep {
		to {
			stroke-dashoffset: -100;
		}
	}
</style>
